import { reactive } from 'vue'
import { fromQuery, toQuery } from 'query-string-parser'
import axios from 'axios'

let latestRequest = 0

export default reactive({
    loading: false,
    initial: true,
    data: {
        items: [],
        facets: [],
        total: 0,
        meta: {},
    },
    filters: {},
    perPage: 20,
    page: 1,
    // De voorwaarden die de pagina zelf oplegt (de FilterSet van bijvoorbeeld een categorie).
    // Die staan los van `filters`: die zijn van de bezoeker en gaan wel de url in, deze niet.
    // Een bezoeker filtert binnen deze lijst en kan er nooit buiten komen.
    baseFilters: {},
    sort: {
        popularity: 'desc'
    },
    append: false,
    target: null,
    // The search query isn't a facet, so it doesn't count as a filter here
    get activeFilterCount () {
        return Object.keys(this.filters).filter(field => field !== 'q').length
    },
    setTarget (target) {
        this.target = target
    },
    init () {
        const params = fromQuery(location.search) || {}

        if (params.f) {
            for (const field in params.f) {
                this.filters[field] = params.f[field].split(',')
            }
        }

        if (params.so) {
            this.sort = params.so
        }

        if (params.pp) {
            this.perPage = params.pp
        }

        if (params.p) {
            this.page = params.p
        }
    },
    resetQueryParams () {
        location.search = ''
    },
    updateQueryParams () {
        window.history.pushState(null, '', window.location.pathname + '?' + decodeURI(this.buildQueryParams()))
    },
    buildQueryParams (override) {
        let params = {}

        if (Object.keys(this.filters).length > 0) {
            params.f = {}
            for (const field in this.filters) {
                if (this.filters[field].length > 0) {
                    params.f[field] = this.filters[field].join(',')
                }
            }
        }

        if (this.perPage > 0) {
            params.pp = this.perPage
        }

        if (this.page > 0) {
            params.p = this.page
        }

        if (this.sort) {
            params.so = this.sort
        }

        params = Object.assign(params, override || {})

        return toQuery(params)
    },
    nextPageUrl () {
        return window.location.pathname + '?' + decodeURI(this.buildQueryParams({
            p: parseInt(this.page) + 1
        }))
    },
    setBaseFilters (baseFilters) {
        this.baseFilters = baseFilters ?? {}
    },
    // Blijft bestaan voor aanroepers die alleen tags meegeven; `t` is de tagsleutel in de api.
    setTags (tags) {
        this.baseFilters = { ...this.baseFilters, t: tags }
    },
    nextPage () {
        this.page = parseInt(this.page) + 1
        this.doAppend()
        this.search()
    },
    resetSort () {
        this.sort = {}
    },
    setSort (field, direction) {
        this.sort[field] = direction
    },
    toggleFilter (field, value, overrideFilter) {
        if (field) {
            if (!this.filters[field]) {
                this.filters[field] = []
            }

            if (overrideFilter) {
                if (value) {
                    this.filters[field][0] = value
                } else {
                    delete this.filters[field]
                }
            } else {
                if (this.filters[field].includes(value)) {
                    this.filters[field].splice(this.filters[field].indexOf(value), 1)
                } else {
                    this.filters[field].push(value)
                }
            }

            if (this.filters[field] && this.filters[field].length === 0) {
                delete this.filters[field]
            }
        }
        this.page = 1
        this.dontAppend()
        this.search()
    },
    dontAppend() {
        this.append = false
    },
    doAppend () {
        this.append = true
    },
    setFilter (field, value) {
        this.filters[field] = value
        this.dontAppend()
        this.search()
    },
    deleteFilter (field) {
        delete this.filters[field]
        this.page = 1
        this.dontAppend()
        this.search()
    },
    // Clears several fields with a single search
    deleteFilters (fields) {
        fields.forEach(field => delete this.filters[field])
        this.page = 1
        this.dontAppend()
        this.search()
    },
    isFilterOptionActive (field, value) {
        return this.filters[field]?.includes(value) ?? false
    },
    getFacetByField (field) {
        return this.data.facets?.find(facet => facet.field === field) ?? null
    },
    reset () {
        this.filters = {}
        this.page = 1
        this.dontAppend()
        this.search()
    },
    // Replaces the sort order and starts over from the first page
    setSorting (field, direction) {
        this.sort = { [field]: direction }
        this.page = 1
        this.dontAppend()
        this.search()
    },
    search () {
        this.loading = true

        // Default params
        let params = {
            pp: this.perPage,
            p: this.page,
        }

        // Filters
        if (Object.keys(this.filters).length > 0) {
            params.f = {}

            for (const field in this.filters) {
                if (this.filters[field].length > 0) {
                    params.f[field] = this.filters[field].join(',')
                }
            }
        }

        // Sort
        if (Object.keys(this.sort).length > 0) {
            params.so = {}

            for (const field in this.sort) {
                params.so[field] = this.sort[field]
            }
        }

        // Voorwaarden van de pagina zelf. Deze gaan wel mee naar de api maar niet de url in:
        // ze horen bij de pagina, niet bij wat de bezoeker heeft aangeklikt.
        params = Object.assign(params, this.baseFilters)

        const request = ++latestRequest

        axios
            .get('/api/bundle/search', {
                params: params
            })
            .then(response => {
                // Ignore responses that were overtaken by a newer search
                if (request !== latestRequest) {
                    return
                }

                if (this.append) {
                    this.data.items = this.data.items.concat(response.data.items)
                    this.data.meta = response.data.meta
                } else {
                    this.data = response.data
                }
            })
            .catch(e => {

            })
            .finally(() => {
                if (request !== latestRequest) {
                    return
                }

                this.loading = false
                this.updateQueryParams()

                if (!this.append && this.target && !this.initial) {
                    window.scrollToElement(this.target)
                }

                this.initial = false
            })
    }
})
